/**
 * Попап виграшу колеса — маленький і мінімалістичний (owner 2026-09-01:
 * «повертаємось до витоків, як було вдруге, тільки трохи більше свята»).
 *
 * Канон той самий, що в «Квест виконано»: max-w-xs, rounded-3xl, p-5,
 * хрестик, анімований емодзі 52, заголовок 1.125rem, приз таблеткою, одна
 * кнопка. Від «свята» — лише тепле світіння в кутку, як у банері оффера.
 * Ніяких таблиць з умовами: усе, що треба знати, людина вже прочитала на
 * сторінці.
 */
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";
import { popupActionsCol } from "@/lib/popupButtons";

/** Кнопки попапу виграшу — ОДНА система (owner 2026-10-26: «кнопки різні, пішла система»): однакова висота
 *  2.5rem, ширина, шрифт і сітка 2rem | текст | 2rem. Текст — по центру ВСІЄЇ кнопки, тож у стовпчику всі підписи
 *  стоять на одній осі, а відстань від тексту до лівого й правого краю кнопки однакова. Кружок ×2 (ліворуч) — у
 *  своїй колонці й на текст не впливає; «Крутити ще раз» і «Оформити підписку» — та сама сітка з порожніми
 *  колонками (owner 27.10: у «Крутити ще раз» кружка немає). */
const pairBtn =
  "relative grid h-[2.5rem] w-full grid-cols-[2rem_1fr_2rem] items-center gap-2 overflow-hidden whitespace-nowrap " +
  "rounded-full px-1 font-display text-[0.8125rem] font-bold leading-none text-white active:scale-[0.98]";
const pairText = "relative text-center";
const pairDot = "relative flex h-8 w-8 items-center justify-center rounded-full bg-white font-display";

/** Подвоєння призу (owner 2026-10-25/26): «кнопку подвоїти — цікавою, виділити, показати множення».
 *  from / to — коротко, «3 дні» → «6 днів». */
export type PrizeDouble = { from: string; to: string; onClick: () => void };

export function WheelPrizePopup({
  open,
  onClose,
  emoji,
  value,
  unit,
  desc,
  ctaLabel,
  onCta,
  againLabel,
  onAgain,
  headline,
  double,
}: {
  open: boolean;
  onClose: () => void;
  emoji: string;
  value: string;
  unit: string;
  /** Короткий рядок під призом — що це означає на практиці. */
  desc?: string;
  /** Куди ведемо після виграшу. Для днів — у стрічку, для бонусів — у
   *  бонуси, для «2 місяці» — скористатись промокодом (owner 2026-09-03). */
  ctaLabel?: string;
  onCta?: () => void;
  /** Друга кнопка «спробувати ще» (owner 2026-09-23): відкриває пакети
   *  обертів або, якщо оберти ще є, одразу крутить. Для призу «ще один оберт»
   *  її не передають — людина й так крутить далі. */
  againLabel?: string;
  onAgain?: () => void;
  /** Заголовок замість «Вітаємо!» — для джекпотів «Джекпот!». */
  headline?: string;
  /** Подвоїти приз (owner 2026-10-25) — лише для частих призів і лише в моменті виграшу, без таймера. */
  double?: PrizeDouble;
}) {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xs overflow-hidden rounded-3xl bg-surface p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,161,53,0.32), rgba(255,161,53,0) 70%)",
          }}
        />
        <PopupCloseButton onClose={onClose} />

        <div className="relative mb-4 text-center">
          <div className="mb-2 flex justify-center">
            <TgEmoji char={emoji} size={52} />
          </div>
          <div className="mb-1.5 font-display text-[1.125rem] font-bold leading-tight text-ink [text-wrap:balance]">
            {headline ?? "Вітаємо!"}
          </div>
          <div className="text-[0.84375rem] leading-relaxed text-ink [text-wrap:balance]">
            Колесо зупинилось на твоєму призі
          </div>

          {/* Приз — щільним рядком, без порожнього поля навколо цифри
              (owner 2026-09-02: «блок з призом — порожнеча»). */}
          {/* Приз по центру, одним стовпчиком: назва і під нею пояснення.
              Емодзі тут НЕ дублюємо — воно вже стоїть над заголовком
              (owner 2026-09-02), а вирівнювання по центру знімає порожнечу
              праворуч, коли назва коротка. */}
          <div className="relative mt-3 rounded-2xl bg-primary/[0.07] px-4 py-3 ring-1 ring-primary/20">
            <div className="font-display text-[1.125rem] font-bold leading-tight text-primary [text-wrap:balance]">
              {value}
              {unit ? ` ${unit}` : ""}
            </div>
            {desc && (
              <div className="mt-1 text-[0.71875rem] leading-snug text-ink-dim [text-wrap:balance]">
                {desc}
              </div>
            )}
          </div>
        </div>

        <div className={"relative " + popupActionsCol}>
          {/* Дві кнопки — різні на вигляд (owner 2026-10-26): «Подвоїти» — помаранчевий множник ×2 із відблиском,
              «Крутити ще раз» — чорна, без іконки. Кнопку «Перейти до стрічки» прибрано зовсім. */}
          {double && (
            <button type="button" onClick={double.onClick} className={pairBtn + " bg-[linear-gradient(100deg,#FF6A00,#FF9A1F_55%,#FFB547)] shadow-[0_0.5rem_1.25rem_-0.5rem_rgba(255,106,0,0.75)]"}>
              <span className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/45 to-transparent course-sheen" />
              {/* Флекс центрує рядок, а не фарбу, і рядок у WebKit і Chrome різний (×2 сідав на 0,7 px вище
                  в одному й на 1 px нижче в іншому) — .cap-center центрує саме цифри (index.css). */}
              <span className={pairDot + " text-[0.8125rem] font-extrabold text-primary"}>
                <span className="cap-center">×2</span>
              </span>
              <span className={pairText}>Подвоїти до {double.to}</span>
              <span />
            </button>
          )}
          {ctaLabel && onCta && (
            <button type="button" onClick={onCta} className={pairBtn + " bg-primary active:bg-primary-deep"}>
              <span />
              <span className={pairText}>{ctaLabel}</span>
              <span />
            </button>
          )}
          {againLabel && onAgain && (
            <button type="button" onClick={onAgain} className={pairBtn + " bg-ink active:bg-ink-2"}>
              {/* Без кружка з іконкою — просто текст по центру (owner 2026-10-27: «Подвоїти» лишаємо як є,
                  у «Крутити ще раз» кружок прибрати). */}
              <span />
              <span className={pairText}>{againLabel}</span>
              <span />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
